<script lang="ts">
import AppNavigationItems from '@/components/common/AppNavigationItems.vue'
import DepositModal from '@/components/modals/DepositModal.vue'
import { Themes } from '@/utils/helper-objects'
import { ThemeType } from '@goodlifeplus/themes'
import { useScreenOrientation } from '@vueuse/core'
import { defineComponent } from 'vue'

/**
 * Displays a navivation header with menu links.
 *
 * @see https://www.makethingsaccessible.com/guides/site-navigation-is-not-an-aria-menu/
 */
export default defineComponent({
  components: {
    AppNavigationItems,
    DepositModal,
  },
  setup() {
    return {
      Themes,
      ThemeType,
      deviceOrientation: useScreenOrientation().orientation,
    }
  },
  data() {
    return {
      open: false,
      computedStyles: {
        top: '65px',
        bottom: '0',
        height: 'calc(100dvh - 65px)',
      },
    }
  },
  computed: {
    routeOptions() {
      return this.$router.currentRoute.value.meta.nav?.header
    },
    hideNav(): boolean {
      return (
        this.$router.currentRoute.value.name === 'campaign' ||
        !!this.routeOptions?.logoImage
      )
    },
  },
  watch: {
    open() {
      this.calculateHeaderHeight()
    },
    deviceOrientation() {
      this.calculateHeaderHeight()
    },
  },
  mounted() {
    this.calculateHeaderHeight()

    document.addEventListener('click', this.closeOnBlur.bind(this))
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeOnBlur.bind(this))
  },
  methods: {
    closeMenu() {
      this.open = !this.open
    },
    closeOnBlur(event: MouseEvent) {
      const target = event.target as HTMLElement

      if (this.open && !target.closest('header')) {
        this.open = false
      }
    },
    calculateHeaderHeight() {
      setTimeout(() => {
        if (this.$refs.header) {
          const computedStyles = getComputedStyle(
            this.$refs.header as HTMLElement
          )

          this.computedStyles.top =
            computedStyles.getPropertyValue('height').replace('px', '') + 'px'

          this.computedStyles.height = `calc(100dvh - ${this.computedStyles.top})`
          this.computedStyles.bottom = '0'
        }
      }, 100)
    },
  },
})
</script>

<template>
  <header
    ref="header"
    class="sticky top-0 flex min-h-16 w-full flex-wrap items-center gap-x-5 gap-y-4 bg-pure-white px-5 py-2 @md/main:gap-x-10 @md/main:px-8"
    :class="routeOptions?.theme ?? Themes.Light"
    aria-labelledby="site-nav-label"
  >
    <div v-if="!hideNav" class="relative block md:hidden">
      <button
        type="button"
        class="flex h-6 w-6 items-center whitespace-nowrap rounded-md sidebar-nav-button"
        :aria-expanded="open"
        @click="closeMenu"
      >
        <svg
          v-show="open"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M20.1034 2.32544C20.5373 1.89152 21.2406 1.89152 21.6746 2.32544C22.1085 2.75935 22.1085 3.46271 21.6746 3.89663L13.5712 12L21.6746 20.1034L21.7505 20.188C22.1065 20.6244 22.0814 21.2678 21.6746 21.6746C21.2678 22.0814 20.6244 22.1065 20.188 21.7505L20.1034 21.6746L12 13.5712L3.89663 21.6746C3.46271 22.1085 2.75935 22.1085 2.32544 21.6746C1.89152 21.2407 1.89152 20.5373 2.32544 20.1034L10.4288 12L2.32544 3.89663L2.24948 3.812C1.89354 3.37558 1.91864 2.73223 2.32544 2.32544C2.73223 1.91865 3.37558 1.89354 3.81199 2.24948L3.89663 2.32544L12 10.4288L20.1034 2.32544Z"
            fill="currentColor"
          />
        </svg>

        <svg
          v-show="!open"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M20.8889 19.7778L21.0028 19.7832C21.563 19.8402 22 20.3137 22 20.8889C22 21.4641 21.563 21.9375 21.0028 21.9946L20.8889 22H3.11111C2.49746 22 2 21.5025 2 20.8889C2 20.2752 2.49746 19.7778 3.11111 19.7778H20.8889ZM20.8889 10.8889L21.0028 10.8943C21.563 10.9513 22 11.4248 22 12C22 12.5752 21.563 13.0487 21.0028 13.1057L20.8889 13.1111H3.11111C2.49746 13.1111 2 12.6137 2 12C2 11.3864 2.49746 10.8889 3.11111 10.8889H20.8889ZM20.8889 2L21.0028 2.00543C21.563 2.06246 22 2.53592 22 3.11111C22 3.68631 21.563 4.15976 21.0028 4.2168L20.8889 4.22222H3.11111C2.49746 4.22222 2 3.72476 2 3.11111C2 2.49746 2.49746 2 3.11111 2H20.8889Z"
            fill="currentColor"
          />
        </svg>
      </button>

      <AppNavigationItems
        v-if="open"
        :open="true"
        class="fixed left-0 overflow-y-auto border-r border-r-slate-200 pb-[70px]"
        :style="computedStyles"
        @close-menu="closeMenu"
      />
    </div>

    <span id="site-nav-label" class="sr-only" hidden>
      {{ BUSINESS_NAME }} Website Navigation
    </span>

    <div
      class="flex flex-grow items-center"
      :class="{ 'justify-center': routeOptions?.logoImage }"
    >
      <RouterLink
        v-if="!routeOptions?.logoImage"
        :to="{ name: 'home' }"
        class="flex-shrink-0 !border-0"
      >
        <span id="business" class="sr-only">{{ BUSINESS_NAME }}</span>

        <img
          :src="
            $theme.type === ThemeType.Club
              ? $theme.assets.logoLight
              : $theme.assets.logo
          "
          :title="`${BUSINESS_NAME} Logo`"
          :alt="`${BUSINESS_NAME} Logo`"
          height="24"
          width="auto"
          class="hidden md:block max-h-12 header-logo"
        />
        <img
          :src="$theme.assets.logoMobile"
          :title="`${BUSINESS_NAME} Logo`"
          :alt="`${BUSINESS_NAME} Logo`"
          height="24"
          width="57"
          class="md:hidden header-mobile-logo"
        />
      </RouterLink>

      <div v-else class="flex items-center gap-4">
        <img
          :src="routeOptions.logoImage.src"
          :title="routeOptions.logoImage.alt"
          :alt="routeOptions.logoImage.alt"
          :class="
            routeOptions.logoImage.alt?.includes('Vodafone')
              ? 'h-12 w-auto'
              : 'h-6 w-auto'
          "
        />
      </div>
    </div>

    <template v-if="!routeOptions?.logoImage">
      <template v-if="$userStore.user && $userStore.isLoggedIn">
        <div class="flex gap-3">
          <RouterLink
            v-tooltip="{
              content: 'Your Level',
              container: 'header',
            }"
            :to="{ name: 'your-level' }"
            class="hidden md:inline"
          >
            <AppIcon
              :icon="Icons.Spaceship"
              class="animate__tada animate__animated animate__hover animate__infinite rounded-full bg-primary p-2 text-pure-white"
            />
          </RouterLink>

          <RouterLink
            v-tooltip="{
              content: 'Daily Spins',
              container: 'header',
            }"
            :to="{ name: 'daily-spins' }"
            class="hidden md:inline"
          >
            <AppIcon
              :icon="Icons.Calendar"
              class="animate__tada animate__animated animate__hover animate__infinite rounded-full bg-primary p-2 text-pure-white"
            />
          </RouterLink>

          <div class="vertical-line !hidden md:!flex" />

          <DepositModal />

          <RouterLink
            class="header-balance"
            :to="{ name: 'members-zone-balance-history' }"
          >
            <p class="text-center text-xs">Balance</p>
            <p id="real-overall-credit" class="font-bold">
              {{ $n($userStore.user.credit.totals.overall / 100, 'currency') }}
            </p>
          </RouterLink>

          <RouterLink
            :to="{ name: 'members-zone' }"
            class="block self-center @md/main:hidden"
          >
            <AppIcon
              :icon="Icons.Profile"
              size="md"
              class="border-none !p-0"
              :class="{ 'text-pure-white': $theme.type === ThemeType.Club }"
            />
          </RouterLink>
        </div>

        <AppButton
          :route="{
            name: 'members-zone',
          }"
          title="Member Zone"
          class="button-primary !hidden text-sm leading-none @md/main:!flex"
        >
          Members Zone
        </AppButton>
      </template>

      <div v-if="$userStore.isLoggedOut" class="flex gap-2">
        <AppRegisterSignInModal :check-on-load="true">
          <template #button="{ openModal }">
            <AppButton
              class="login-button !h-min min-w-fit flex-1 bg-primary !py-2 leading-none"
              :title="$t('label.login')"
              @click="openModal('login')"
            >
              <p class="flex items-center text-sm font-semibold md:text-base">
                {{ $t('label.login') }}
              </p>
            </AppButton>

            <AppButton
              class="button-primary !h-min min-w-fit flex-1 !py-2 leading-none"
              :title="$t('label.login')"
              @click="openModal('register')"
            >
              <p class="flex items-center text-sm font-semibold md:text-base">
                Sign Up
              </p>
            </AppButton>
          </template>
        </AppRegisterSignInModal>
      </div>
    </template>
  </header>
</template>

<style lang="scss" scoped>
header {
  z-index: 2147483630;
  transition: all 0.15s ease-in-out;

  // .slide-from-left-enter-active,
  // .slide-from-left-leave-active {
  //   transition: all 0.5s ease;
  // }
  // .slide-from-left-enter-from,
  // .slide-from-left-leave-to {
  //   transform: translateX(-100%);
  // }

  // .slide-from-left {
  //   @apply bottom-0 left-0 rounded-t-lg max-md:w-full md:h-dvh md:rounded-r-lg;
  // }
}
</style>
